import { notifications } from "@mantine/notifications"
import { addVideoToPlaylistApiV1PlaylistsPlaylistIdVideosPost, createPlaylistApiV1PlaylistsPost, deletePlaylistApiV1PlaylistsIdDelete, deleteVideoFromPlaylistApiV1PlaylistsPlaylistIdVideosVideoIdDelete, downloadPlaylistApiV1PlaylistsPlaylistIdDownloadPost, getPublicPlaylistsApiV1PlaylistsPublicGet, getUserPlaylistsApiV1PlaylistsGet, Playlist, PlaylistParams, PlaylistVideo, PlaylistVideoParams, updatePlaylistApiV1PlaylistsPlaylistIdPatch } from "../client"
import { showErrorNotification } from "../utils/errors"

export const getUserPlaylists = async (token: string): Promise<Playlist[] | null> => {
  try {
    const res = await getUserPlaylistsApiV1PlaylistsGet({
      auth: `Bearer ${token}`
    })
    if (res.error !== undefined) throw new Error('Error getting user playlists')
    return res.data || []

  } catch {
    return null
  }
}

export const getPublicPlaylists = async (): Promise<Playlist[] | null> => {
  try {
    const res = await getPublicPlaylistsApiV1PlaylistsPublicGet()
    if (res.error !== undefined) throw new Error('Error getting public playlists')
    return res.data || []

  } catch {
    return null
  }
}

export const createPlaylist = async (params: PlaylistParams, token: string): Promise<Playlist | null> => {
  try {
    const res = await createPlaylistApiV1PlaylistsPost({
      auth: `Bearer ${token}`,
      body: params
    })
    if (res.error !== undefined) { throw Error("Could not create playlist") }
    
    const playlist = res.data
    notifications.show({
      color: playlist ? 'green' : 'yellow',
      message: playlist ? `Created playlist '${playlist.name}'` : `Playlist '${params.name}' already exists`
    })
    return playlist

  } catch (error) {
    showErrorNotification({
      id: 'create-playlist-error',
      title: 'Playlist Creation Failed',
      message: `Could not create playlist '${params.name}'. Please try again.`,
      error
    })
    return null
  }
}

export const updatePlaylist = async (id: string, name: string | null, description: string | null, priv: boolean | null, thumbnailUrl: string | null, token: string): Promise<Playlist | null> => {
  try {
    const res = await  updatePlaylistApiV1PlaylistsPlaylistIdPatch({
      auth: `Bearer ${token}`,
      path: { playlist_id: id },
      query: {
        description,
        name,
        priv,
        thumbnail_url: thumbnailUrl
      }
    })
    
    if (!res.response.ok) throw new Error("Error updating playlists")
    
    if (!res.data) {
      showErrorNotification({
        id: `update-playlist-error-${id}`,
        title: 'Update Failed',
        message: 'Unable to update playlist details.'
      })
      return null
    }
    return res.data

  } catch (error) {
    showErrorNotification({
      id: `update-playlist-error-${id}`,
      title: 'Update Failed',
      message: 'Unable to update playlist details.',
      error
    })
    return null
  }
}

export const deletePlaylist = async (playlist: Playlist, token: string) => {
  try {
    const result = await deletePlaylistApiV1PlaylistsIdDelete({
      auth: `Bearer ${token}`,
      path: {
        id: playlist.id
      }
    })

    if (result.response.ok) {
      notifications.show({
        color: 'green',
        message: `Playlist '${playlist.name}' deleted`
      })
    } else {
      showErrorNotification({
        id: `delete-playlist-error-${playlist.id}`,
        title: 'Delete Failed',
        message: `Could not delete playlist '${playlist.name}'.`
      })
    }
  } catch (error) {
    showErrorNotification({
      id: `delete-playlist-error-${playlist.id}`,
      title: 'Delete Failed',
      message: `Could not delete playlist '${playlist.name}'.`,
      error
    })
  }
}

export const addVideoToPlaylist = async (params: PlaylistVideoParams, playlistId: string, token: string): Promise<PlaylistVideo | null > => {
  try {
    const res = await addVideoToPlaylistApiV1PlaylistsPlaylistIdVideosPost({
      auth: `Bearer ${token}`,
      path: {
        playlist_id: playlistId,
      },
      body: params
    })
    if (res.error !== undefined) throw new Error("error adding video to playlist")
    return res.data

  } catch (error) {
    showErrorNotification({
      id: `add-video-error-${playlistId}`,
      title: 'Add Video Failed',
      message: 'Could not add video to playlist.',
      error
    })
    return null
  }
}

export const deleteVideoFromPlaylist = async (videoId: string, playlistId: string, token: string) => {
  try {
    const result = await deleteVideoFromPlaylistApiV1PlaylistsPlaylistIdVideosVideoIdDelete({
      auth: `Bearer ${token}`,
      path: {
        playlist_id: playlistId,
        video_id: videoId
      }
    })

    if (result.response.ok) {
      notifications.show({
        color: 'green',
        message: `Video deleted`
      })
    } else {
      showErrorNotification({
        id: `delete-video-error-${videoId}`,
        title: 'Delete Failed',
        message: 'Could not remove video from playlist.'
      })
    }

  } catch (error) {
    showErrorNotification({
      id: `delete-video-error-${videoId}`,
      title: 'Delete Failed',
      message: 'Could not remove video from playlist.',
      error
    })
  }
}

export const downloadPlaylist = async (playlistId: string, token: string) => {
  try {
    const res = await downloadPlaylistApiV1PlaylistsPlaylistIdDownloadPost({
      auth: `Bearer ${token}`,
      path: {
        playlist_id: playlistId,
      }
    })

    if (!res.response.ok) throw new Error("Error downloading playlist")
    const blob = res.data as Blob
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'videos.zip';
    link.click();
    URL.revokeObjectURL(url);

  } catch (error) {
    showErrorNotification({
      id: `download-playlist-error-${playlistId}`,
      title: 'Download Failed',
      message: 'Unable to download playlist videos. Please try again.',
      error
    })
  }
}